// NOTICE: KAPTION-INTEGRATION
// Helper function to build starred children for the sidebar
// Used by both KaptionSidebar.vue and KaptionSidebarV2.vue

/**
 * Build menu items for starred sidebar items
 * Resolves starred item references to actual navigable menu items
 * Items that don't exist in the current account are filtered out
 *
 * @param {Array} starredItems - Array of starred item references [{type, id}]
 * @param {Object} options - Dependencies needed to build items
 * @param {Array} options.inboxes - Available inboxes
 * @param {Array} options.labels - Available labels
 * @param {Array} options.teams - Available teams
 * @param {Array} options.assistants - Available assistants
 * @param {Array} options.folders - Available custom views (folders)
 * @param {Function} options.accountScopedRoute - Route helper
 * @param {Function} options.h - Vue's h function for rendering
 * @param {Object} options.ChannelLeaf - ChannelLeaf component
 * @returns {Array} Array of menu item objects
 */
export function buildStarredChildren(starredItems, options) {
  const {
    inboxes = [],
    labels = [],
    teams = [],
    assistants = [],
    folders = [],
    accountScopedRoute,
    h,
    ChannelLeaf,
  } = options;

  return starredItems
    .map(item => {
      switch (item.type) {
        // Support both 'channel' and 'inbox' types for backwards compatibility
        // The topbar uses 'inbox', while older code may have used 'channel'
        case 'channel':
        case 'inbox': {
          const inbox = inboxes.find(i => i.id === item.id);
          if (!inbox) return null;
          return {
            name: `Starred-Channel-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          };
        }

        case 'label': {
          const label = labels.find(l => l.id === item.id);
          if (!label) return null;
          return {
            name: `Starred-Label-${label.id}`,
            label: label.title,
            icon: h('span', {
              class:
                'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          };
        }

        case 'team': {
          const team = teams.find(tm => tm.id === item.id);
          if (!team) return null;
          return {
            name: `Starred-Team-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          };
        }

        case 'assistant': {
          const assistant = assistants.find(a => a.id === item.id);
          if (!assistant) return null;
          return {
            name: `Starred-Assistant-${assistant.id}`,
            label: assistant.name,
            to: accountScopedRoute('conversation_kaption_assistant', {
              assistantId: assistant.id,
            }),
          };
        }

        case 'folder': {
          const folder = folders.find(f => f.id === item.id);
          if (!folder) return null;
          return {
            name: `Starred-Folder-${folder.id}`,
            label: folder.name,
            to: accountScopedRoute('folder_conversations', { id: folder.id }),
          };
        }

        default:
          return null;
      }
    })
    .filter(Boolean);
}
